import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

// Generic searchable replacement for a plain <select> of STRING options
// (partner names, branch names, etc.) — filters the list as you type
// instead of requiring a long scroll through a native <select>.
//
// For picking an admin/operator by name OR mobile number, use
// OperatorSearchSelect instead — this one only matches against the
// option text itself, since plain string lists like partner/branch
// names have no separate mobile number to search by.
//
// Props:
//   options   — string[]
//   value     — the currently selected value, or `allValue` for "no filter"
//   onChange  — (value) => void
//   allLabel  — label for the "no filter" row (default "All")
//   allValue  — the value onChange receives for "no filter" (default "all")
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  allLabel = "All",
  allValue = "all",
  placeholder,
  className = "",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return options;
    return options.filter((o) => (o || "").toLowerCase().includes(text));
  }, [options, query]);

  // Row 0 is always the "All" row; rows 1..n are `filtered`. Keeping
  // that one flat list is what makes arrow-key navigation simple below.
  const rowCount = filtered.length + 1;

  const select = (val) => {
    onChange(val);
    setQuery("");
    setOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, rowCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex === 0) {
        select(allValue);
      } else if (highlightIndex > 0) {
        const opt = filtered[highlightIndex - 1];
        if (opt !== undefined) select(opt);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setHighlightIndex(-1);
    }
  };

  const isFiltered = value && value !== allValue;

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <input
        type="text"
        className={`searchable-select-input ${className}`}
        placeholder={placeholder || (isFiltered ? value : allLabel)}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlightIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {isFiltered && !query && (
        <button
          type="button"
          className="searchable-select-clear"
          onClick={() => select(allValue)}
          tabIndex={-1}
          aria-label="Clear filter"
        >
          ×
        </button>
      )}

      {open && (
        <div className="searchable-select-dropdown">
          <div
            className={`searchable-select-option ${
              value === allValue ? "selected" : ""
            } ${highlightIndex === 0 ? "highlighted" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              select(allValue);
            }}
            onMouseEnter={() => setHighlightIndex(0)}
          >
            {allLabel}
          </div>

          {filtered.length === 0 ? (
            <div className="searchable-select-empty">No matches</div>
          ) : (
            filtered.map((o, index) => (
              <div
                key={o}
                className={`searchable-select-option ${
                  o === value ? "selected" : ""
                } ${highlightIndex === index + 1 ? "highlighted" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(o);
                }}
                onMouseEnter={() => setHighlightIndex(index + 1)}
              >
                {o}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
